import * as THREE from 'three/webgpu';

/**
 * @file The other half of the draw-call budget. Merged geometry handles the
 * unique stuff; anything that repeats (streetlights, cars, AC units, rocks,
 * trees, barrels) goes through an `InstancedBatch` — one draw call per prop
 * type, regardless of how many of them there are.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DAY/NIGHT TRICK
 * ─────────────────────────────────────────────────────────────────────────────
 * Merged geometry carries baked lighting in `color`/`color1` per vertex.
 * Instances cannot: every copy shares one geometry. So each INSTANCE carries two
 * RGB tints instead, uploaded as `InstancedBufferAttribute`s:
 *
 *   `iColD`  day   — sample 3 sun rays at placement time (T4's job)
 *   `iColN`  night — sample the {@link LightProbeGrid} at placement time
 *
 * and T2's material multiplies by `mix(iColD, iColN, uNight)`. Result: a
 * streetlight in an alley is dark by day and glows at night, at zero runtime
 * cost, with one draw call for all 300 of them.
 *
 * The material is supplied by the CALLER (T2's `bakedInstancedMaterial`) so this
 * class stays render-layer agnostic and unit-testable in node.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PER-INSTANCE CULLING, AND WHY THE TINTS ARE THE TRAP
 * ─────────────────────────────────────────────────────────────────────────────
 * One draw call for 400 vents is the whole point of this class, and it is also
 * why three.js's frustum culler can do nothing with it: the test is PER OBJECT,
 * and this object's bounding sphere spans the map (`city.wild.rock`: 98
 * instances, radius 389 m). The camera stands inside it every frame. Measured on
 * the shipped city: 1,758 prop instances submitted, 4 of them inside the
 * frustum.
 *
 * {@link InstancedBatch#cull} is the answer — a sphere test per INSTANCE, then
 * a compaction of the survivors into the front of the instance buffers and
 * `mesh.count = visible`. It is OPT-IN: with no frustum nothing is allocated,
 * nothing is copied, and the draw is byte-for-byte the one this class always
 * made. Dev scenes, galleries and the node tests therefore pay nothing.
 *
 * ⚠ THE COMPACTION MOVES THREE BUFFERS, NOT ONE. `iColD`/`iColN` are indexed by
 * the same instance slot as the matrix, so a matrix-only compaction leaves every
 * surviving prop wearing the day and night tint of whatever instance used to sit
 * at its new slot — a rooftop unit lit as if it stood in an alley, changing as
 * the camera moves. That failure is subtle enough to read as a bake bug, so
 * `tests/city.test.mjs` pins it directly.
 *
 * ⚠ A LOGICAL INDEX IS NOT A DRAW SLOT once culling is on. `add()` returns the
 * logical index and `setColors(i, ...)` keeps taking it — the class holds the
 * `logical -> slot` map so that a caller re-tinting instance 7 (the probe pass
 * does exactly this, for every instance) cannot tint whichever prop happens to
 * be drawing at slot 7. The authoritative colours live in `_srcD`/`_srcN` from
 * the first cull onwards; `_colD`/`_colN` (the attribute arrays, whose identity
 * the TSL material captured at construction) become the DRAW arrays.
 *
 * @module engine/build/InstancedBatch
 */

/** @typedef {[number, number, number]|{r: number, g: number, b: number}} ColorLike */

/** Plan default for the night tint of an unlit instanced prop. */
const NIGHT_DEFAULT = [0.25, 0.3, 0.45];

/**
 * @param {ColorLike|null|undefined} c
 * @param {Float32Array} dst
 * @param {number} off
 * @param {number[]} fallback
 */
function writeColor(c, dst, off, fallback) {
  if (!c) {
    dst[off] = fallback[0];
    dst[off + 1] = fallback[1];
    dst[off + 2] = fallback[2];
    return;
  }
  if (Array.isArray(c)) {
    dst[off] = c[0];
    dst[off + 1] = c[1];
    dst[off + 2] = c[2];
    return;
  }
  dst[off] = c.r;
  dst[off + 1] = c.g;
  dst[off + 2] = c.b;
}

export class InstancedBatch {
  /**
   * @param {THREE.BufferGeometry} geo Prop geometry, ideally non-indexed and
   *   flat (`prims.js` output). **The batch takes ownership**: `commit()`
   *   attaches `iColD`/`iColN` to it, so do not share one geometry between two
   *   batches — build a second one, or `clone()` first.
   * @param {THREE.Material|null} material From T2 (`bakedInstancedMaterial`).
   *   Not touched here beyond being handed to the `InstancedMesh`. May be `null`
   *   and assigned to `batch.material` before `commit()` — which is what you
   *   want when the material is built from this batch's own `iColD`/`iColN`.
   * @param {number} max Hard cap on instances. Buffers are allocated up front,
   *   so size it to the real budget (a city streetlight batch is ~300, not 10k).
   */
  constructor(geo, material, max) {
    if (!geo) throw new Error('InstancedBatch: geometry is required');
    if (!(max > 0)) throw new Error('InstancedBatch: max must be > 0');

    this.geometry = geo;
    this.material = material;
    this.max = max | 0;
    this.count = 0;

    /** Column-major 4x4 per instance, laid out exactly like `instanceMatrix`. */
    this._mat = new Float32Array(this.max * 16);
    this._colD = new Float32Array(this.max * 3);
    this._colN = new Float32Array(this.max * 3);

    // Attached HERE, not in commit(), so a caller can build the TSL material
    // from the live attributes before anything is placed:
    //   const batch = new InstancedBatch(geo, null, 300);
    //   batch.material = bakedInstancedMaterial({
    //     attrD: instancedBufferAttribute(geo.getAttribute('iColD'), 'vec3'),
    //     attrN: instancedBufferAttribute(geo.getAttribute('iColN'), 'vec3'),
    //   });
    // The buffers stay at `max` length while the draw only reads `count` of
    // them, which is legal and avoids a copy at commit time.
    geo.setAttribute('iColD', new THREE.InstancedBufferAttribute(this._colD, 3));
    geo.setAttribute('iColN', new THREE.InstancedBufferAttribute(this._colN, 3));

    /** @type {THREE.InstancedMesh|null} */
    this.mesh = null;
    this._overflow = 0;

    // ── culling state, all null until the first cull() with a frustum ────────
    /** World sphere per LOGICAL instance as `(x, y, z, r)`. @type {Float32Array|null} */
    this._sph = null;
    /** `logical -> draw slot`, `-1` when culled. @type {Int32Array|null} */
    this._slot = null;
    /** Authoritative day tints once `_colD` has become a draw array. @type {Float32Array|null} */
    this._srcD = null;
    /** Authoritative night tints. @type {Float32Array|null} */
    this._srcN = null;
    /** Keyed sun visibility, 2 bytes per instance — see {@link enableSunVis}. @type {Uint8Array|null} */
    this._vis = null;
    /** Authoritative visibility once `_vis` has become a draw array. @type {Uint8Array|null} */
    this._srcV = null;
    /** Instances the last cull submitted. */
    this._visible = 0;
  }

  /**
   * Opts this batch into per-instance KEYED sun visibility: attaches `iSunVis`
   * (`Uint8` vec2, normalized — `.x` = key a, `.y` = key b, mixed by `uSunMix`
   * in `render/nodes.js#sunShadeTint`).
   *
   * OPT-IN for the same reason culling is: a gallery or a test that never calls
   * this pays nothing and renders bit-for-bit what it always did. Call BEFORE
   * building the material — `bakedInstancedMaterial`'s `attrVis` wants the live
   * attribute, exactly like `iColD`.
   *
   * Defaults to 255/255 (fully sunlit), so an instance nobody ever writes keys
   * for renders exactly as it would without the feature.
   *
   * ⚠ LOCKSTEP. This is a third buffer the compaction moves — see the header
   * warning at line 42. Every path that moves `iColD` moves this too, and the
   * three-instance cull-the-middle test pins it.
   *
   * @returns {THREE.InstancedBufferAttribute} The live attribute.
   */
  enableSunVis() {
    let a = this.geometry.getAttribute('iSunVis');
    if (a) return a;
    this._vis = new Uint8Array(this.max * 2).fill(255);
    a = new THREE.InstancedBufferAttribute(this._vis, 2, true);
    a.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('iSunVis', a);
    return a;
  }

  /** True once {@link cull} has taken over the attribute arrays. */
  get culled() {
    return this._srcD !== null;
  }

  /**
   * @param {THREE.Matrix4} matrix World transform for this instance.
   * @param {{ colorDay?: ColorLike, colorNight?: ColorLike }} [opts]
   *   `colorDay` defaults to white, `colorNight` to `[0.25, 0.3, 0.45]`.
   * @returns {number} Instance index, or `-1` when the batch is full (which is
   *   logged once — a full batch means the level's budget was set too low).
   */
  add(matrix, { colorDay, colorNight } = {}) {
    if (this.count >= this.max) {
      if (this._overflow++ === 0) {
        console.warn(`[InstancedBatch] full at ${this.max} instances; extra adds are dropped`);
      }
      return -1;
    }
    const i = this.count++;
    const e = matrix.elements;
    const o = i * 16;
    for (let k = 0; k < 16; k++) this._mat[o + k] = e[k];
    // `_srcD` is null until the first cull, so the normal path writes straight
    // into the attribute arrays exactly as it always did.
    writeColor(colorDay, this._srcD ?? this._colD, i * 3, [1, 1, 1]);
    writeColor(colorNight, this._srcN ?? this._colN, i * 3, NIGHT_DEFAULT);
    // Adding after a cull is not a path anything takes today (the mesh is built
    // at `max` and committed once), but a stale sphere table would silently
    // drop the new instance, so drop the table instead and let it rebuild.
    this._sph = null;
    return i;
  }

  /**
   * Builds the `InstancedMesh`: uploads the matrices, flags `iColD`/`iColN` for
   * upload, sets `count` to what was actually added (NOT `max`), and computes
   * bounds so frustum culling is correct.
   *
   * Idempotent — calling it twice returns the same mesh.
   *
   * @returns {THREE.InstancedMesh|null} `null` when nothing was added, so the
   *   caller can skip parenting an empty draw.
   */
  commit() {
    if (this.mesh) return this.mesh;
    if (this.count === 0) return null;

    const mesh = new THREE.InstancedMesh(this.geometry, this.material, this.max);
    mesh.instanceMatrix.array.set(this._mat);
    mesh.instanceMatrix.needsUpdate = true;
    mesh.count = this.count;

    const d = this.geometry.getAttribute('iColD');
    const nn = this.geometry.getAttribute('iColN');
    const v = this.geometry.getAttribute('iSunVis');
    if (d) d.needsUpdate = true;
    if (nn) nn.needsUpdate = true;
    if (v) v.needsUpdate = true;

    mesh.computeBoundingBox();
    mesh.computeBoundingSphere();
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();

    this.mesh = mesh;
    this._visible = this.count;
    return mesh;
  }

  // ── per-instance frustum culling (opt-in) ───────────────────────────────────

  /**
   * Submits only the instances the frustum can see.
   *
   * Every survivor is COMPACTED into the front of the instance buffers —
   * matrix, `iColD` and `iColN` together — and `mesh.count` is set to how many
   * there were. A batch with nothing visible is hidden outright, which is one
   * fewer draw rather than a zero-instance one.
   *
   * The instances are treated as STATIC: the sphere table is derived once from
   * the matrices that were added. A caller that moves one afterwards must go
   * through {@link setMatrix}, which keeps the table honest — writing `_mat`
   * directly would leave a prop culled against the sphere it used to have.
   *
   * @param {{sphereVisible: (x: number, y: number, z: number, r: number) => boolean}|null} frustum
   *   Anything with `Frustum`'s test (`engine/render/Culling.js`). **Null puts
   *   every instance back on screen**, which is also the state a batch that has
   *   never been culled is already in — so a caller that never passes a frustum
   *   pays nothing at all.
   * @param {number} [guard=0] Metres added to every instance radius. This is
   *   the slack for a visible set that is REBUILT ON A THRESHOLD rather than
   *   every frame (see `CameraWatch`): between two rebuilds a frustum plane can
   *   sweep `move + sin(turn) * distance` metres, and an instance inside that
   *   band would pop in a frame late at the screen edge. The caller owns both
   *   numbers, so it owns the guard — see `levels/shared/props.js#PROP_CULL`.
   * @returns {number} Instances submitted (`count` when not culling).
   */
  cull(frustum, guard = 0) {
    const mesh = this.mesh;
    if (!mesh) return this.count;
    if (!frustum) {
      if (this.culled) this._restore();
      return this.count;
    }
    if (!this._srcD) {
      // First cull: `_colD`/`_colN` become the DRAW arrays (their identity is
      // what the material captured, so they can never be swapped out), and the
      // authored values move to `_srcD`/`_srcN`. `_vis` follows the same split
      // when the batch carries it.
      this._srcD = Float32Array.from(this._colD);
      this._srcN = Float32Array.from(this._colN);
      if (this._vis) this._srcV = Uint8Array.from(this._vis);
    }
    if (!this._slot || this._slot.length < this.count) this._slot = new Int32Array(this.max);
    if (!this._sph) this._buildSpheres();

    const S = /** @type {Float32Array} */ (this._sph);
    const slot = /** @type {Int32Array} */ (this._slot);
    const srcD = /** @type {Float32Array} */ (this._srcD);
    const srcN = /** @type {Float32Array} */ (this._srcN);
    const dstD = this._colD;
    const dstN = this._colN;
    const src = this._mat;
    const dst = /** @type {Float32Array} */ (mesh.instanceMatrix.array);

    let w = 0;
    for (let i = 0; i < this.count; i++) {
      const s = i * 4;
      if (!frustum.sphereVisible(S[s], S[s + 1], S[s + 2], S[s + 3] + guard)) {
        slot[i] = -1;
        continue;
      }
      slot[i] = w;
      const a = i * 16;
      const b = w * 16;
      for (let k = 0; k < 16; k++) dst[b + k] = src[a + k];
      const c = i * 3;
      const d = w * 3;
      dstD[d] = srcD[c];
      dstD[d + 1] = srcD[c + 1];
      dstD[d + 2] = srcD[c + 2];
      dstN[d] = srcN[c];
      dstN[d + 1] = srcN[c + 1];
      dstN[d + 2] = srcN[c + 2];
      if (this._srcV) {
        // The third lockstep buffer — a missed compaction here is a prop
        // wearing another prop's SHADOW, which reads as a key bug two files
        // away from the cause.
        this._vis[w * 2] = this._srcV[i * 2];
        this._vis[w * 2 + 1] = this._srcV[i * 2 + 1];
      }
      w++;
    }

    mesh.count = w;
    mesh.visible = w > 0;
    mesh.instanceMatrix.needsUpdate = true;
    this._touchColors();
    this._visible = w;
    return w;
  }

  /** Puts the identity layout back: every instance drawn, in logical order. */
  _restore() {
    const mesh = this.mesh;
    this._colD.set(/** @type {Float32Array} */ (this._srcD));
    this._colN.set(/** @type {Float32Array} */ (this._srcN));
    if (this._srcV) this._vis.set(this._srcV);
    if (this._slot) for (let i = 0; i < this.count; i++) this._slot[i] = i;
    if (mesh) {
      /** @type {Float32Array} */ (mesh.instanceMatrix.array).set(this._mat);
      mesh.instanceMatrix.needsUpdate = true;
      mesh.count = this.count;
      mesh.visible = true;
    }
    this._touchColors();
    this._visible = this.count;
  }

  /**
   * One world sphere per instance, from the PROTOTYPE's bounding sphere pushed
   * through each instance matrix.
   *
   * The radius is scaled by the largest column length of the matrix's upper 3x3
   * — the countryside scatter puts rocks on a 2.28x matrix and trees on 1.45x,
   * so a prototype radius on its own would under-cover them and they would pop
   * at the screen edge. Taking the LARGEST of the three axes is the
   * conservative choice for a non-uniform scale: the sphere it produces
   * contains the transformed geometry.
   */
  _buildSpheres() {
    this._sph = new Float32Array(Math.max(1, this.count) * 4);
    for (let i = 0; i < this.count; i++) this._sphereFor(i);
  }

  /**
   * Re-derives ONE instance's world sphere from its matrix. Split out of
   * {@link _buildSpheres} so {@link setMatrix} can refresh a single moving
   * instance without walking the whole batch.
   * @param {number} i
   */
  _sphereFor(i) {
    const S = this._sph;
    if (!S) return;
    const geo = this.geometry;
    if (!geo.boundingSphere) geo.computeBoundingSphere();
    const bs = geo.boundingSphere;
    const px = bs ? bs.center.x : 0;
    const py = bs ? bs.center.y : 0;
    const pz = bs ? bs.center.z : 0;
    const r0 = bs ? bs.radius : 0;
    const m = this._mat;
    const o = i * 16;
    const sx = Math.hypot(m[o], m[o + 1], m[o + 2]);
    const sy = Math.hypot(m[o + 4], m[o + 5], m[o + 6]);
    const sz = Math.hypot(m[o + 8], m[o + 9], m[o + 10]);
    const w = i * 4;
    S[w] = m[o] * px + m[o + 4] * py + m[o + 8] * pz + m[o + 12];
    S[w + 1] = m[o + 1] * px + m[o + 5] * py + m[o + 9] * pz + m[o + 13];
    S[w + 2] = m[o + 2] * px + m[o + 6] * py + m[o + 10] * pz + m[o + 14];
    S[w + 3] = r0 * Math.max(sx, sy, sz);
  }

  /**
   * Moves one instance AFTER `commit()` — a streetlight going over, a bench
   * plank tumbling (`engine/systems/Breakables.js`).
   *
   * `i` is the LOGICAL index, exactly like {@link setColors}, and for exactly
   * the same reason: under culling the draw slot is a stranger. Three things
   * have to move together or the batch lies about itself —
   *
   *  • `_mat`, which is authoritative and what the next cull compacts FROM;
   *  • the instance's world sphere, or the prop goes on being culled against
   *    the volume it occupied before it fell;
   *  • the DRAW slot, if it currently has one — that is what makes the motion
   *    visible this frame rather than at the next `CameraWatch` rebuild.
   *
   * A prop with no draw slot is off screen by definition, so skipping it costs
   * nothing visible: it comes back correctly posed on the next rebuild.
   *
   * @param {number} i
   * @param {THREE.Matrix4} matrix
   */
  setMatrix(i, matrix) {
    if (i < 0 || i >= this.count) return;
    const e = matrix.elements;
    const o = i * 16;
    for (let k = 0; k < 16; k++) this._mat[o + k] = e[k];
    if (this._sph) this._sphereFor(i);
    const mesh = this.mesh;
    if (!mesh) return;
    const s = this.culled ? (this._slot ? this._slot[i] : -1) : i;
    if (s < 0) return;
    const dst = /** @type {Float32Array} */ (mesh.instanceMatrix.array);
    const b = s * 16;
    for (let k = 0; k < 16; k++) dst[b + k] = e[k];
    mesh.instanceMatrix.needsUpdate = true;
  }

  /** Flags the instance attributes for upload. */
  _touchColors() {
    const d = this.geometry.getAttribute('iColD');
    const n = this.geometry.getAttribute('iColN');
    const v = this.geometry.getAttribute('iSunVis');
    if (d) d.needsUpdate = true;
    if (n) n.needsUpdate = true;
    if (v) v.needsUpdate = true;
  }

  /**
   * Writes one instance's keyed sun visibility. `i` is the LOGICAL index, like
   * {@link setColors}, and the culled-mirror dance is the same one.
   *
   * @param {number} i
   * @param {number} a 0..255 — visibility at the resident key `a`.
   * @param {number} b 0..255 — visibility at the resident key `b`.
   */
  setSunVis(i, a, b) {
    if (!this._vis || i < 0 || i >= this.count) return;
    const V = this._srcV ?? this._vis;
    V[i * 2] = a;
    V[i * 2 + 1] = b;
    if (this.culled) {
      const s = this._slot ? this._slot[i] : -1;
      if (s >= 0) {
        this._vis[s * 2] = a;
        this._vis[s * 2 + 1] = b;
      }
    }
    const attr = this.geometry.getAttribute('iSunVis');
    if (attr) attr.needsUpdate = true;
  }

  /**
   * Re-tints one instance AFTER `commit()` (e.g. the bake finished later, or a
   * prop got destroyed). Marks the attributes dirty for you.
   *
   * `i` is the LOGICAL index — the one `add()` returned — whether or not the
   * batch is being culled. See the header: the draw slot is not the same number
   * once instances are compacted, and writing at `i` would tint a stranger.
   *
   * @param {number} i
   * @param {ColorLike} [colorDay]
   * @param {ColorLike} [colorNight]
   */
  setColors(i, colorDay, colorNight) {
    if (i < 0 || i >= this.count) return;
    const D = this._srcD ?? this._colD;
    const N = this._srcN ?? this._colN;
    if (colorDay) writeColor(colorDay, D, i * 3, [1, 1, 1]);
    if (colorNight) writeColor(colorNight, N, i * 3, NIGHT_DEFAULT);
    if (this.culled) {
      // Mirror into the draw slot, if this instance is on screen at all.
      const s = this._slot ? this._slot[i] : -1;
      if (s >= 0) {
        const a = i * 3;
        const b = s * 3;
        if (colorDay) {
          this._colD[b] = D[a];
          this._colD[b + 1] = D[a + 1];
          this._colD[b + 2] = D[a + 2];
        }
        if (colorNight) {
          this._colN[b] = N[a];
          this._colN[b + 1] = N[a + 1];
          this._colN[b + 2] = N[a + 2];
        }
      }
    }
    this._touchColors();
  }

  /**
   * @returns {{ count: number, max: number, tris: number, dropped: number,
   *   visible: number, visibleTris: number, culling: boolean }}
   *   `tris` is the FULL triangle cost (per-instance tris x count) and stays
   *   the budget number every level prints; `visible`/`visibleTris` are what
   *   the last {@link cull} actually submitted, reported so the win cannot rot
   *   silently.
   */
  stats() {
    const pos = this.geometry.getAttribute('position');
    const per = pos ? (this.geometry.index ? this.geometry.index.count : pos.count) / 3 : 0;
    const visible = this.culled ? this._visible : this.count;
    return {
      count: this.count,
      max: this.max,
      tris: per * this.count,
      dropped: this._overflow,
      visible,
      visibleTris: per * visible,
      culling: this.culled,
    };
  }

  /** Disposes the geometry (which the batch owns). The material is the caller's. */
  dispose() {
    this.mesh?.dispose?.();
    this.geometry?.dispose?.();
    this.mesh = null;
  }
}
